import { LOCALE_TAGS } from '../../src/i18n/index';
import { T, TN, api, errorText, esc, getLocale, todayIn, addDaysISO } from '../shared/client';

/**
 * Reports: covers and sources over a period, shift occupancy, turn times and
 * no-shows, with a CSV export of the reservations. Plain bars drawn in HTML so
 * it works without a chart library and prints well.
 */
interface Opts {
  restaurantId: string;
  timezone: string;
  toast: (msg: string, isErr?: boolean) => void;
  onClose: () => void;
}

export function openReportsScreen(opts: Opts): void {
  const root = document.createElement('div');
  root.className = 'cfg-root rp-root';
  document.body.appendChild(root);
  document.body.classList.add('cfg-open');
  const base = `/v1/restaurants/${opts.restaurantId}/reports`;

  const today = todayIn(opts.timezone);
  let from = addDaysISO(today, -29);
  let to = today;
  let data: { summary: any; turn: any; occ: any; sales: any } | null = null;
  let busy = false;

  const num = (n: number) => new Intl.NumberFormat(LOCALE_TAGS[getLocale()]).format(n);
  const dayLabel = (iso: string) =>
    new Intl.DateTimeFormat(LOCALE_TAGS[getLocale()], { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${iso}T00:00:00Z`));
  const weekday = (n: number) =>
    new Intl.DateTimeFormat(LOCALE_TAGS[getLocale()], { weekday: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(2024, 0, n))); // 1 Jan 2024 is a Monday

  async function load() {
    busy = true;
    render();
    try {
      const q = `from=${from}&to=${to}`;
      const [summary, turn, occ, sales] = await Promise.all([
        api('GET', `${base}/summary?${q}`),
        api('GET', `${base}/turn-times?${q}`),
        api('GET', `${base}/occupancy?${q}`),
        api('GET', `${base}/sales?${q}`),
      ]);
      data = { summary, turn, occ, sales };
    } catch (e) {
      opts.toast(errorText(e), true);
    }
    busy = false;
    render();
  }

  const bar = (label: string, value: number, max: number, extra = '') =>
    `<div class="hbar"><span>${esc(label)}</span><span class="track"><span class="fill" style="width:${max ? (value / max) * 100 : 0}%;display:block"></span></span><span>${esc(extra || num(value))}</span></div>`;

  function kpi(label: string, value: string, hint = ''): string {
    return `<div class="kpi"><span class="muted small">${esc(label)}</span><strong>${esc(value)}</strong>${hint ? `<span class="small muted">${esc(hint)}</span>` : ''}</div>`;
  }

  function salesBlock(x: any): string {
    const m = (v: number) => new Intl.NumberFormat(LOCALE_TAGS[getLocale()], { style: 'currency', currency: x.currency }).format(v / 100);
    if (!x.checks)
      return `<section class="card" style="margin-bottom:12px"><h3>${esc(T('r.sales.title'))}</h3><p class="muted">${esc(T('r.sales.none'))}</p></section>`;
    const dayMax = Math.max(1, ...x.daily.map((d: any) => d.grossMinor));
    const srcMax = Math.max(1, ...x.bySource.map((d: any) => d.grossMinor));
    return `<div class="kpis">
        ${kpi(T('r.sales.revenue'), m(x.grossMinor), T('r.sales.checks_n', { n: x.checks }))}
        ${kpi(T('r.sales.avg_check'), m(x.averageCheckMinor))}
        ${kpi(T('r.sales.per_cover'), m(x.perCoverMinor), T('r.sales.matched', { n: x.matchedChecks }))}
        ${x.tipsMinor ? kpi(T('r.sales.tips'), m(x.tipsMinor)) : ''}
      </div>
      <div class="rp-grid" style="margin-bottom:12px">
        <section class="card"><h3>${esc(T('r.sales.by_day'))}</h3>${x.daily.map((d: any) => bar(dayLabel(d.day), d.grossMinor, dayMax, m(d.grossMinor))).join('')}</section>
        <section class="card"><h3>${esc(T('r.sales.by_source'))}</h3>${x.bySource.map((d: any) => bar(d.source === 'unmatched' ? T('r.sales.unmatched') : (T(`h.source.${d.source}`) === `h.source.${d.source}` ? d.source : T(`h.source.${d.source}`)), d.grossMinor, srcMax, m(d.grossMinor))).join('')}</section>
      </div>`;
  }

  function body(): string {
    if (!data) return `<p class="muted">${esc(busy ? T('common.loading') : T('r.none'))}</p>`;
    const s = data.summary;
    const delta = s.previousCovers ? Math.round(((s.covers - s.previousCovers) / s.previousCovers) * 100) : null;
    const dayMax = Math.max(1, ...s.daily.map((d: any) => d.reserved + d.walkIn));
    const src = s.bySource as any[];
    const srcMax = Math.max(1, ...src.map((x) => x.covers));
    const sizeMax = Math.max(1, ...s.byPartySize.map((x: any) => x.parties));
    const dowMax = Math.max(1, ...s.byWeekday.map((x: any) => x.average));
    const turnRows = (data.turn.rows as any[])
      .map(
        (r) => `<tr><td>${r.size >= 10 ? '10+' : r.size}</td><td class="num">${r.parties}</td><td class="num">${r.actualMinutes} ${esc(T('r.min'))}</td>
        <td class="num">${r.plannedMinutes} ${esc(T('r.min'))}</td>
        <td class="num ${r.difference > 10 ? 'over' : ''}">${r.difference > 0 ? '+' : ''}${r.difference}</td></tr>`,
      )
      .join('');
    // Occupancy by shift: total covers per shift name, and per day for the busiest shift names.
    const byShift = new Map<string, { reserved: number; walkIn: number }>();
    for (const r of data.occ.rows as any[]) {
      const e = byShift.get(r.shift) ?? { reserved: 0, walkIn: 0 };
      e.reserved += r.reserved;
      e.walkIn += r.walkIn;
      byShift.set(r.shift, e);
    }
    const shiftMax = Math.max(1, ...[...byShift.values()].map((e) => e.reserved + e.walkIn));
    return `
      <div class="kpis">
        ${kpi(T('r.covers'), num(s.covers), delta == null ? '' : T('r.vs_prev', { d: `${delta > 0 ? '+' : ''}${delta}%` }))}
        ${kpi(T('r.parties'), num(s.parties))}
        ${kpi(T('r.walkins'), num(s.walkInCovers))}
        ${kpi(T('r.noshows'), num(s.noShows), T('r.rate', { p: s.noShowRate }))}
        ${kpi(T('r.cancellations'), num(s.cancellations))}
        ${kpi(T('r.first_time'), num(s.firstTimeGuests), T('r.repeat', { n: s.repeatGuests }))}
        ${kpi(T('r.lead'), T('r.days', { n: s.avgLeadDays }))}
      </div>
      ${salesBlock(data.sales)}
      <div class="rp-grid">
        <section class="card"><h3>${esc(T('r.by_day'))}</h3>${
          s.daily.length
            ? `<div class="daybars">${s.daily
                .map(
                  (d: any) => `<div class="daybar" title="${esc(`${dayLabel(d.day)}: ${d.reserved} + ${d.walkIn}`)}">
                    <span class="stack"><i class="w" style="height:${(d.walkIn / dayMax) * 100}%"></i><i class="r" style="height:${(d.reserved / dayMax) * 100}%"></i></span>
                    <small>${esc(dayLabel(d.day))}</small></div>`,
                )
                .join('')}</div><p class="small muted"><i class="key r"></i>${esc(T('r.reserved'))} <i class="key w"></i>${esc(T('r.walkin'))}</p>`
            : `<p class="muted">${esc(T('r.no_data'))}</p>`
        }</section>
        <section class="card"><h3>${esc(T('r.by_source'))}</h3>${src.map((x) => bar(T(`h.source.${x.source}`) === `h.source.${x.source}` ? x.source : T(`h.source.${x.source}`), x.covers, srcMax)).join('') || `<p class="muted">${esc(T('r.no_data'))}</p>`}</section>
        <section class="card"><h3>${esc(T('r.by_size'))}</h3>${(s.byPartySize as any[]).map((x) => bar(x.size >= 10 ? '10+' : String(x.size), x.parties, sizeMax)).join('') || `<p class="muted">${esc(T('r.no_data'))}</p>`}</section>
        <section class="card"><h3>${esc(T('r.by_weekday'))}</h3>${(s.byWeekday as any[]).map((x) => bar(weekday(x.weekday), x.average, dowMax, `${num(x.average)} ${T('r.avg')}`)).join('') || `<p class="muted">${esc(T('r.no_data'))}</p>`}</section>
        <section class="card"><h3>${esc(T('r.by_shift'))}</h3>${[...byShift.entries()]
          .map(([name, e]) => bar(name, e.reserved + e.walkIn, shiftMax, `${num(e.reserved)} + ${num(e.walkIn)}`))
          .join('') || `<p class="muted">${esc(T('r.no_data'))}</p>`}</section>
        <section class="card"><h3>${esc(T('r.turn'))}</h3>${
          turnRows
            ? `<table class="cfg-table"><thead><tr><th>${esc(T('r.party'))}</th><th class="num">${esc(T('r.parties'))}</th><th class="num">${esc(T('r.actual'))}</th><th class="num">${esc(T('r.planned'))}</th><th class="num">${esc(T('r.diff'))}</th></tr></thead><tbody>${turnRows}</tbody></table>
               <p class="small muted">${esc(T('r.turn_hint'))}</p>`
            : `<p class="muted">${esc(T('r.no_data'))}</p>`
        }</section>
      </div>
      <p class="small muted">${esc(TN('r.footnote', s.parties))}</p>`;
  }

  function render() {
    const kept = (root.querySelector('.rp-main') as HTMLElement | null)?.scrollTop ?? 0;
    const presets: [string, string, string][] = [
      ['7', addDaysISO(today, -6), today],
      ['30', addDaysISO(today, -29), today],
      ['90', addDaysISO(today, -89), today],
    ];
    root.innerHTML = `
      <div class="cfg-bar"><strong>${esc(T('r.title'))}</strong>
        <span class="spacer"></span><button data-do="close">${esc(T('common.close'))}</button></div>
      <div class="rp-controls cfg-row">
        <label>${esc(T('r.from'))} <input type="date" id="rp-from" value="${esc(from)}" max="${esc(to)}"></label>
        <label>${esc(T('r.to'))} <input type="date" id="rp-to" value="${esc(to)}"></label>
        <span class="seg">${presets.map(([n, f, t]) => `<button data-do="preset" data-f="${f}" data-t="${t}" aria-pressed="${from === f && to === t}">${esc(T('r.last', { n }))}</button>`).join('')}</span>
        <span class="spacer"></span>
        <a class="btn" href="${base}/reservations.csv?from=${from}&to=${to}" download>${esc(T('r.export'))}</a>
      </div>
      <main class="rp-main" tabindex="0" style="padding:0 16px 24px;overflow:auto">${body()}</main>`;
    const m = root.querySelector('.rp-main') as HTMLElement | null;
    if (m) m.scrollTop = kept;
  }

  root.addEventListener('change', (ev) => {
    const el = ev.target as HTMLInputElement;
    if (el.id === 'rp-from' && el.value) from = el.value;
    else if (el.id === 'rp-to' && el.value) to = el.value;
    else return;
    if (from > to) to = from;
    void load();
  });
  root.addEventListener('click', (ev) => {
    const b = (ev.target as HTMLElement).closest('[data-do]') as HTMLElement | null;
    if (!b) return;
    if (b.dataset.do === 'close') {
      root.remove();
      document.body.classList.remove('cfg-open');
      opts.onClose();
    } else if (b.dataset.do === 'preset') {
      from = b.dataset.f!;
      to = b.dataset.t!;
      void load();
    }
  });
  root.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') (root.querySelector('[data-do="close"]') as HTMLElement | null)?.click();
  });

  void load();
}
